import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { numberFormat, transformFieldWithLang } from './../../utils/functions';
import { getCartSnapshot } from './../../utils/cartSnapshot';
import './PendingBookingModal.css';

/**
 * สรุปการจองที่ค้างรอชำระเงิน -- ใช้ตัวเดียวกันทั้งหน้าตะกร้าและแถบบนหน้าอื่น
 *
 * ถ้ามีสำเนาตะกร้า (เฉพาะการจองที่ผ่านหน้าตะกร้า) จะวาดแบบละเอียด: ราคาแยกรายห้อง
 * ของเสริม ส่วนลด เงินประกัน ยอดที่ต้องจ่ายตอนนี้/วันเข้าพัก และผู้ติดต่อ
 *
 * การจองห้องเดี่ยว ห้องยืดหยุ่น และลานกางเต็นท์ไม่เคยผ่านตะกร้า จึงไม่มีสำเนานั้น
 * -- วาดจากสิ่งที่มีครบทุกเส้นทางแทน (ตัวชี้ใน localStorage + ข้อมูลที่ถามจากเซิร์ฟเวอร์)
 * ยอมให้ข้อมูลไม่เท่ากัน ดีกว่ามี modal สองแบบที่หน้าตาไม่เหมือนกัน
 */
const PendingBookingModal = ({ show, onHide, pending, rooms = [], stay = null, onResume }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'th';

  if (!pending?.id) return null;

  const money = (n) => numberFormat(Number(n || 0), true);
  const roomNames = (rooms || []).map((r) => transformFieldWithLang(r, 'name', lang)).filter(Boolean);

  const renderFallback = () => (
    <>
      <div className="pbm__amount">
        <div className="pbm__amount-label">{t('pay_total_amount')}</div>
        <div className="pbm__amount-value">
          {money(pending.amount)}<small>{t('currency_unit')}</small>
        </div>
        {!!pending.booking_no && (
          <div className="pbm__ref">{t('booking_no')} <b>{pending.booking_no}</b></div>
        )}
      </div>

      {!!stay?.start_date && (
        <div className="pbm__stay">
          <div className="pbm__stay-col">
            <span className="pbm__stay-label">{t('check_in')}</span>
            <span className="pbm__stay-date">{moment(stay.start_date).format('DD/MM/YYYY')}</span>
          </div>
          <span className="pbm__stay-nights">
            {moment(stay.end_date).add(1, 'days').diff(moment(stay.start_date), 'days')} {t('nights')}
          </span>
          <div className="pbm__stay-col">
            <span className="pbm__stay-label">{t('check_out')}</span>
            <span className="pbm__stay-date">{moment(stay.end_date).add(1, 'days').format('DD/MM/YYYY')}</span>
          </div>
        </div>
      )}

      {!!roomNames.length && (
        <div className="pbm__section">
          <div className="pbm__section-title">{t('room')}</div>
          {roomNames.map((name, i) => (
            <div className="pbm__room" key={`pbm-fb-${i}`}>
              <div className="pbm__room-head">
                <div className="pbm__room-name">{name}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* บอกตรงๆ ว่าทำไมไม่มีรายละเอียดราคา จะได้ไม่คิดว่าระบบแสดงไม่ครบ */}
      <div className="pbm__section">
        <p className="pbm__note">{t('pending_payment_detail_partial')}</p>
      </div>
    </>
  );

  return (
    <Modal show={show} onHide={onHide} centered scrollable contentClassName="pbm">
      <Modal.Header closeButton><Modal.Title>{t('booking_summary')}</Modal.Title></Modal.Header>
      <Modal.Body>
        {(() => {
          const snap = getCartSnapshot();
          const sum = snap?.summary;

          // สรุปยอดถูกคำนวณและเก็บไว้ตอนสร้างการจอง ไม่ได้คิดใหม่ตรงนี้
          // เพราะสูตรราคาทั้งหมดอ่านจากตะกร้าซึ่งถูกล้างไปแล้ว (ดู handleSubmitBooking)
          // ไม่มีสำเนาตะกร้า = จองห้องเดี่ยว/ยืดหยุ่น/ลานกางเต็นท์ ซึ่งไม่เคยผ่านหน้าตะกร้า
          // วาดเท่าที่มีจริงแทน (เลขที่จอง · ห้อง · วันเข้าพัก · ยอดที่ต้องจ่าย)
          if (!sum?.rooms?.length) return renderFallback();

          const nights = snap.startDate && snap.endDate
            ? moment(snap.endDate).diff(moment(snap.startDate), 'days')
            : 0;
          const form = snap.form || {};

          const grandTotal = Number(sum.total || 0) + Number(sum.security_deposit || 0);
          const payNow = Number(sum.pay_now || 0);
          const payLater = Math.max(0, grandTotal - payNow);

          const contact = [
            [t('contact_name'), form.contact_name],
            [t('contact_tel'), form.contact_tel_1],
            [t('email'), form.contact_email],
          ].filter(([, v]) => !!v);

          return (
            <>
              <div className="pbm__amount">
                <div className="pbm__amount-label">{t('pay_total_amount')}</div>
                <div className="pbm__amount-value">
                  {money(payNow)}<small>{t('currency_unit')}</small>
                </div>
                {!!pending?.booking_no && (
                  <div className="pbm__ref">{t('booking_no')} <b>{pending.booking_no}</b></div>
                )}
              </div>

              {!!snap.startDate && (
                <div className="pbm__stay">
                  <div className="pbm__stay-col">
                    <span className="pbm__stay-label">{t('check_in')}</span>
                    <span className="pbm__stay-date">{moment(snap.startDate).format('DD/MM/YYYY')}</span>
                  </div>
                  <span className="pbm__stay-nights">{nights > 0 ? `${nights} ${t('nights')}` : '—'}</span>
                  <div className="pbm__stay-col">
                    <span className="pbm__stay-label">{t('check_out')}</span>
                    <span className="pbm__stay-date">
                      {snap.endDate ? moment(snap.endDate).format('DD/MM/YYYY') : '—'}
                    </span>
                  </div>
                </div>
              )}

              <div className="pbm__section">
                <div className="pbm__section-title">{t('room')}</div>
                {sum.rooms.map((room, idx) => {
                  const guests = [
                    room.guest_qty > 0 ? `${t('adult')} ${room.guest_qty}` : null,
                    room.guest_baby_qty > 0 ? `${t('children')} ${room.guest_baby_qty}` : null,
                  ].filter(Boolean).join(' · ');

                  // ทุกบรรทัดที่มีราคาต้องขึ้นแยก ไม่งั้นยอดรวมของห้องจะดูเหมือนคำนวณผิด
                  const lines = [
                    ...(room.additional_guest_qty > 0 ? [{
                      label: `${t('guest_additional')} · ${t('adult')} ${room.additional_guest_qty}`,
                      price: room.additional_guest_price,
                    }] : []),
                    ...(room.additional_baby_qty > 0 ? [{
                      label: `${t('guest_additional')} · ${t('children')} ${room.additional_baby_qty}`,
                      price: room.additional_baby_price,
                    }] : []),
                    ...(room.addons || []).map((a) => ({
                      label: a.qty > 1 ? `${a.label} × ${a.qty}` : a.label,
                      price: a.total_price,
                    })),
                    ...(room.pets || []).map((x) => ({
                      label: x.qty > 1 ? `${x.label} × ${x.qty}` : x.label,
                      price: x.total_price,
                    })),
                  ];

                  return (
                    <div className="pbm__room" key={`pbm-room-${idx}`}>
                      <div className="pbm__room-head">
                        <div className="pbm__room-name">
                          {transformFieldWithLang(room, 'name', lang) || `${t('room')} ${idx + 1}`}
                          {!!guests && <span className="pbm__room-sub">{t('guest_qty')} : {guests}</span>}
                        </div>
                        <div className="pbm__room-price">{money(room.room_price)}</div>
                      </div>

                      {lines.map((line, li) => (
                        <div className="pbm__line" key={`pbm-line-${idx}-${li}`}>
                          <span>{line.label}</span>
                          <span className="pbm__line-price">+{money(line.price)}</span>
                        </div>
                      ))}

                      {room.discount > 0 && (
                        <div className="pbm__line pbm__line--minus">
                          <span>{t('discount_coupon')}</span>
                          <span className="pbm__line-price">−{money(room.discount)}</span>
                        </div>
                      )}

                      {lines.length + (room.discount > 0 ? 1 : 0) > 0 && (
                        <div className="pbm__room-total">
                          <span>{t('total_amount')}</span>
                          <span>{money(room.total)}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="pbm__section pbm__totals">
                <div className="pbm__total-row">
                  <span>{t('total_amount')}</span>
                  <span>{money(sum.total)}</span>
                </div>
                {Number(sum.security_deposit || 0) > 0 && (
                  <div className="pbm__total-row">
                    <span>{t('security_deposit')}</span>
                    <span>{money(sum.security_deposit)}</span>
                  </div>
                )}
                <div className="pbm__total-row pbm__total-row--pay">
                  <span>
                    {t('pay_now')}
                    {sum.payment_type === 'deposit' && (
                      <span className="pbm__tag">{t('booking_deposit')}</span>
                    )}
                  </span>
                  <span>{money(payNow)}</span>
                </div>
                {payLater > 0 && (
                  <div className="pbm__total-row pbm__total-row--later">
                    <span>{t('payment_method_pay_on_stay')}</span>
                    <span>{money(payLater)}</span>
                  </div>
                )}
              </div>

              {!!contact.length && (
                <div className="pbm__section">
                  <div className="pbm__section-title">{t('contact_name')}</div>
                  <dl className="pbm__kv">
                    {contact.map(([label, value]) => (
                      <div className="pbm__kv-row" key={`pbm-kv-${label}`}>
                        <dt>{label}</dt>
                        <dd>{value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
            </>
          );
        })()}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>{t('close')}</Button>
        {!!onResume && (
          <Button variant="dark" onClick={onResume}>{t('pending_payment_resume')}</Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default PendingBookingModal;
